import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Bot, Headphones, Loader2 } from 'lucide-react';
import { SupportAPI } from '../api';
import { toast } from 'sonner';

const TONE = {
  user: 'bg-[#0F766E] text-white',
  bot: 'bg-[#EEF2FF] text-[#0A1551]',
  agent: 'bg-[#F0F1FB] text-[#0A1551]',
};

const Bubble = ({ m }) => (
  <div className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div className={`max-w-[85%] px-3 py-2 rounded-lg text-[13px] leading-snug ${TONE[m.sender] || TONE.agent}`}>
      {m.sender !== 'user' && (
        <div className="flex items-center gap-1 text-[11px] font-semibold text-[#0F766E] mb-0.5">
          {m.sender === 'bot' ? <Bot className="w-3 h-3" /> : <Headphones className="w-3 h-3" />}
          {m.sender_name || (m.sender === 'bot' ? 'Assistant' : 'Customer Service')}
        </div>
      )}
      {m.body}
    </div>
  </div>
);

const SupportChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [mode, setMode] = useState('bot');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const load = () => SupportAPI.myMessages().then((d) => {
      if (!alive) return;
      setMessages(d.messages);
      setMode(d.mode || 'bot');
    }).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => { alive = false; clearInterval(t); };
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const res = await SupportAPI.send(body);
      setMessages((prev) => [...prev, res.message, ...(res.reply ? [res.reply] : [])]);
      setMode(res.mode || mode);
      setDraft('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send your message');
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    try {
      const res = await SupportAPI.escalate();
      setMode('human');
      if (res.reply) setMessages((prev) => [...prev, res.reply]);
    } catch (err) {
      toast.error('Could not reach customer service — please try again');
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="support-chat-open"
          className="fixed bottom-[86px] right-5 z-[60] flex items-center gap-2 px-4 py-3 rounded-full bg-[#0A1551] text-white shadow-lg hover:bg-[#141F6B] transition-colors">
          <MessageCircle className="w-5 h-5" />
          <span className="text-[14px] font-semibold hidden sm:inline">Need help?</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-[86px] right-5 z-[60] w-[min(92vw,360px)] h-[min(70vh,520px)] bg-white border border-[#E3E5F5] rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="support-chat-panel">
          <div className="px-4 py-3 bg-[#0A1551] text-white flex items-center gap-2">
            {mode === 'human' ? <Headphones className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            <div className="flex-1">
              <p className="text-[14px] font-bold leading-tight" data-testid="support-chat-mode">{mode === 'human' ? 'Customer Service' : 'Cathie Assistant'}</p>
              <p className="text-[11px] text-white/70">{mode === 'human' ? 'Online 24/7 · usually replies in a few minutes' : 'Instant answers · a human is one tap away'}</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-chat-close" className="p-1 rounded hover:bg-white/10" aria-label="Close chat"><X className="w-4 h-4" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="support-chat-messages">
            {messages.length === 0 && (
              <p className="text-[13px] text-[#6F76A7] text-center mt-6">Ask me about signing your agreement, your PDF copy, or using the workspace.</p>
            )}
            {messages.map((m) => <Bubble key={m.id} m={m} />)}
            {sending && <div className="flex items-center gap-1.5 text-[12px] text-[#6F76A7]"><Loader2 className="w-3 h-3 animate-spin" /> thinking…</div>}
            <div ref={endRef} />
          </div>

          {mode === 'bot' && (
            <button onClick={escalate} data-testid="support-talk-to-human"
              className="mx-3 mb-2 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[#D5D7ED] text-[12px] font-semibold text-[#0A1551] hover:bg-[#F5F6FB]">
              <Headphones className="w-3.5 h-3.5" /> Talk to customer service instead
            </button>
          )}

          {mode === 'human' && (
            <p className="mx-3 mb-2 px-3 py-2 rounded-md bg-[#E6F4F2] text-[12px] text-[#0B5F58]" data-testid="support-availability-note">
              Customer service is online 24/7 — we usually reply within a few minutes.
            </p>
          )}

          <form onSubmit={send} className="p-3 pt-0 flex items-center gap-2">
            <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type your message" data-testid="support-chat-input"
              className="flex-1 px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] focus:outline-none focus:border-[#0A1551]" />
            <button type="submit" disabled={!draft.trim() || sending} data-testid="support-chat-send"
              className="p-2.5 rounded-md bg-[#0F766E] text-white disabled:opacity-50" aria-label="Send message"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
};

export default SupportChatWidget;
